import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from 'core/layout/MainLayout';
import {
    Box,
    Button,
    Container,
    Typography,
    Paper,
    Stack,
    CircularProgress,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { RenderCVHttpService } from '../services';

function RenderCVPreviewPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [document, setDocument] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    useEffect(() => {
        const loadDocument = async () => {
            try {
                const data = await RenderCVHttpService.getPreview(parseInt(id));
                setDocument(data);
            } catch (error) {
                console.error('Error loading document:', error);
                enqueueSnackbar('Failed to load CV document', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };
        if (id) {
            loadDocument();
        }
    }, [id, enqueueSnackbar]);

    const handleDownload = async () => {
        try {
            await RenderCVHttpService.downloadPDF(parseInt(id));
            enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
        } catch (error) {
            console.error('Error downloading PDF:', error);
            enqueueSnackbar('Failed to download PDF', { variant: 'error' });
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await RenderCVHttpService.deleteDocument(parseInt(id));
            enqueueSnackbar('CV deleted successfully', { variant: 'success' });
            navigate('/cv');
        } catch (error) {
            console.error('Error deleting document:', error);
            enqueueSnackbar('Failed to delete CV', { variant: 'error' });
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <Container maxWidth="lg" sx={{ mt: 6, mb: 8 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </MainLayout>
        );
    }

    if (!document) {
        return (
            <MainLayout>
                <Container maxWidth="lg" sx={{ mt: 6, mb: 8 }}>
                    <Alert severity="error">CV document not found</Alert>
                </Container>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <Container maxWidth="lg" sx={{ mt: 6, mb: 8 }}>
                <Stack spacing={3}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/cv')}
                        >
                            Back to CVs
                        </Button>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownload}
                            >
                                Download PDF
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/cv/rendercv/${id}/edit`)}
                            >
                                Edit
                            </Button>
                            <IconButton
                                color="error"
                                onClick={() => setDeleteDialogOpen(true)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Stack>
                    </Box>

                    {/* PDF Preview */}
                    <Paper
                        elevation={0}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            overflow: 'hidden',
                            minHeight: 800,
                        }}
                    >
                        {document.pdf_url ? (
                            <Box
                                component="iframe"
                                src={document.pdf_url}
                                sx={{
                                    width: '100%',
                                    height: '1200px',
                                    border: 'none',
                                }}
                                title="CV Preview"
                            />
                        ) : (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Alert severity="warning">PDF file not available</Alert>
                            </Box>
                        )}
                    </Paper>

                    {/* Document Info */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        <Stack spacing={1}>
                            <Typography variant="h6" fontWeight={600}>
                                {document.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Template: {document.template_name || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Created: {new Date(document.created_at).toLocaleDateString()}
                            </Typography>
                        </Stack>
                    </Paper>
                </Stack>
            </Container>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete CV</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this CV? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                    >
                        {deleting ? <CircularProgress size={20} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </MainLayout>
    );
}

export default RenderCVPreviewPage;

